import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Form,
  Input,
  Button,
  notification,
  Modal,
} from 'antd';
import { MailOutlined } from '@ant-design/icons';
import './Login.css';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

const ForgotPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch('/backend/forgot_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await response.json();

      if (response.ok) {
        notification.success({
          message: 'Request Sent',
          description: 'A reset link has been sent to your email.',
          duration: 5,
        });
        form.resetFields();
        navigate('/login');
      } else {
        setModalMessage(data.error || 'An error occurred.');
        setIsModalVisible(true);
      }
    } catch (error) {
      setModalMessage('Network error. Please check your connection.');
      setIsModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = () => {
    notification.error({
      message: 'Validation Failed',
      description: 'Please check your email.',
      duration: 3,
    });
  };

  const validateEmail = (_, value) => {
    if (!value) return Promise.reject(new Error('Email required!'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Invalid email format!'));
    }
    return Promise.resolve();
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setModalMessage('');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          background: '#ffffff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 15px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
        aria-label="NH Guide Header"
      >
        <Title level={4} style={{ margin: 0, color: '#4a90e2', fontWeight: 700, fontSize: 20 }}>
          NH Guide
        </Title>
      </Header>

      <Content style={{ flex: 1, padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="login-form-container">
          <Title
            level={3}
            style={{ textAlign: 'center', color: '#2c3e50', fontWeight: 800, marginBottom: 15, fontSize: 28 }}
            aria-label="Forgot Password Title"
          >
            Reset Password
          </Title>
          <Text
            style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: 25, display: 'block', fontSize: 16, fontStyle: 'italic' }}
            aria-label="Instruction Message"
          >
            Enter your email to receive a reset link.
          </Text>
          <Form
            form={form}
            name="forgot-password"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            style={{ maxWidth: '500px', width: '100%' }}
            validateTrigger="onBlur"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[{ validator: validateEmail }]}
              hasFeedback
            >
              <Input
                prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="user@gmail.com"
                size="large"
                className="custom-input"
                aria-label="Email Field"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="custom-button"
                style={{ width: '100%', background: 'linear-gradient(90deg, #2c3e50, #34495e)', borderColor: '#2c3e50' }}
                loading={isLoading}
                aria-label="Send Reset Link Button"
              >
                Send Reset Link
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: 'center',
          padding: '15px 0',
          background: '#ffffff',
          boxShadow: '0 -2px 15px rgba(0, 0, 0, 0.05)',
          color: '#7f8c8d',
          fontSize: 12,
        }}
        aria-label="Footer"
      >
        <Text style={{ display: 'block', marginTop: 5, fontSize: 10 }}>
          © 2025 NH Guide. All rights reserved.
        </Text>
      </Footer>

      <Modal
        title="Error"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={handleModalOk}
            style={{ background: '#4a90e2', border: 'none', borderRadius: '8px', padding: '0 20px' }}
            aria-label="Close Modal"
          >
            OK
          </Button>,
        ]}
        style={{ top: '20%', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)' }}
        bodyStyle={{ textAlign: 'center', fontSize: '16px', color: '#2c3e50', padding: '25px' }}
        aria-label="Error Modal"
      >
        <p>{modalMessage}</p>
      </Modal>
    </Layout>
  );
};

export default ForgotPassword;